import { useMemo, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { useLocale } from "../primitives/useLocale";
import type {
  Exercise,
  Program as ProgramT,
  ResolvedProgramTemplate,
  UnitsPreference,
} from "@jackedlogs/shared";

import type { UiTheme } from "../theme";
import { IconButton } from "../primitives/IconButton";
import { useArmedConfirm } from "../primitives/useArmedConfirm";
import { ProgramPreview } from "../Programs/ProgramPreview";
import { makePlanStyles } from "../Programs/programsPlanStyles";
import { PlanPreview } from "../Plans/PlanPreview";
import { difficultyLabel } from "./difficultyLabel";
import { LibraryItemCardActions } from "./LibraryItemCardActions";
import { libraryItemName, type LibraryItem } from "./useLibrary";

// One item in the Library browser (Create-hub §S3): name + status/difficulty
// chip, a meta line, and the actions row — Vedi ▾ (inline preview, the
// house collapsed-by-default pattern), then browse actions (Segui, Clona)
// and, since HR-S4 retired the Home management lists, the management
// actions for personal rows (Avvia · Pubblica/Unfollow · Clona, then
// Modifica/Elimina as pencil/trash IconButtons — HR-7) — all optional
// handlers, so hosts without a capability (e.g. no logger on web → no
// Avvia) simply don't render it. Delete confirm is a platform seam like
// MyProgramRow's: 'host' = the mount confirms (mobile Alert), 'inline' =
// two-tap on the card (web). Text-action + chip styles come from
// makePlanStyles so the idiom can't drift from the Programs rows.

export type LibraryItemCardProps = {
  theme: UiTheme;
  dark: boolean;
  item: LibraryItem;
  catalog: Exercise[] | null;
  resolveName: (slug: string, snapshot?: string) => string;
  // Display unit for prescribed loads in the plan preview. Default kg.
  unit?: UnitsPreference;
  /** True while THIS item's clone POST is in flight. */
  cloning: boolean;
  /** True while any other clone is in flight (disable, keep label). */
  cloneBusy: boolean;
  /** Official item already cloned (by name or in this session). */
  alreadyCloned: boolean;
  /** True while THIS item's follow PATCH is in flight. */
  following?: boolean;
  /** ⭐ state. Only meaningful when onToggleFavorite is wired. */
  favorited?: boolean;
  /** True while THIS item's favorite write is in flight. */
  favoriteBusy?: boolean;
  onClone: (item: LibraryItem) => void;
  /** Wired only for personal published-not-followed programs. */
  onFollow?: () => void;
  /** Wired only for favoritable items (personal programs/plans). */
  onToggleFavorite?: () => void;
  /** Personal plans on hosts with a logger (mobile) — starts the session. */
  onStart?: () => void;
  /** True while THIS item's start resolution is in flight. */
  starting?: boolean;
  /** Personal draft programs → published (status archived). */
  onPublish?: () => void;
  /** Personal followed programs → published (status archived). */
  onUnfollow?: () => void;
  /** True while THIS item's status PATCH (publish/unfollow) is in flight. */
  statusBusy?: boolean;
  /** Personal rows — opens the composer on the item. */
  onEdit?: () => void;
  /** Personal rows. Confirmation follows confirmDelete. */
  onDelete?: () => void;
  /** 'host' = the mount confirms (mobile Alert); 'inline' = two-tap
   *  Elimina → Confermi? on the card (web — RN Alert is a no-op there). */
  confirmDelete?: "host" | "inline";
};

/** Preview-only Program synthesized from a resolved template — the
 *  structural fields ProgramPreview reads (sessions/cycleWeeks) are real,
 *  identity/lifecycle fields are display stubs that never leave the
 *  client (a clone POST goes through programCloneBody, never this). */
const templatePreviewProgram = (tpl: ResolvedProgramTemplate): ProgramT => ({
  id: tpl.id,
  createdBy: "jackedlogs",
  assignedTo: ["jackedlogs"],
  name: tpl.name,
  description: tpl.description,
  cycleWeeks: tpl.cycleWeeks,
  sessionsPerWeek: tpl.sessionsPerWeek,
  sessions: tpl.sessions,
  status: "draft",
  createdAt: "1970-01-01T00:00:00.000Z",
  updatedAt: "1970-01-01T00:00:00.000Z",
});

export function LibraryItemCard({
  theme,
  dark,
  item,
  catalog,
  resolveName,
  unit = "kg",
  cloning,
  cloneBusy,
  alreadyCloned,
  following = false,
  favorited = false,
  favoriteBusy = false,
  onClone,
  onFollow,
  onToggleFavorite,
  onStart,
  starting = false,
  onPublish,
  onUnfollow,
  statusBusy = false,
  onEdit,
  onDelete,
  confirmDelete = "inline",
}: LibraryItemCardProps) {
  const { t } = useTranslation();
  const lang = useLocale();
  const styles = makeStyles(theme);
  const planStyles = makePlanStyles(theme);
  const [open, setOpen] = useState(false);
  // HR-8: auto-reverting confirm — the inline "Confirm?" step must have an
  // exit (the icon row has no room for a separate cancel affordance).
  const [confirming, setConfirming] = useArmedConfirm();

  const onDeletePress = () => {
    if (!onDelete) return;
    if (confirmDelete === "inline" && !confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    onDelete();
  };

  const name = libraryItemName(item, lang);

  const chipLabel = useMemo(() => {
    if (item.source === "official") {
      return difficultyLabel(item.template.difficulty, t);
    }
    if (item.kind === "program") {
      return item.program.status === "active"
        ? t("program.chipFollowed", { defaultValue: "Following" })
        : item.program.status === "draft"
          ? t("program.chipDraft", { defaultValue: "Draft" })
          : t("program.chipPublished", { defaultValue: "Published" });
    }
    const s = item.plan.status;
    return t(
      `plans.status${s.charAt(0).toUpperCase()}${s.slice(1)}` as const,
      { defaultValue: s },
    );
  }, [item, t]);

  const meta =
    item.kind === "program"
      ? t("templates.programMeta", {
          weeks:
            item.source === "personal"
              ? item.program.cycleWeeks
              : item.template.cycleWeeks,
          sessions:
            item.source === "personal"
              ? item.program.sessionsPerWeek
              : item.template.sessionsPerWeek,
          defaultValue: "{{weeks}}-week cycle · {{sessions}}× / week",
        })
      : t("templates.planMeta", {
          n:
            item.source === "personal"
              ? item.plan.entries.length
              : item.template.entries.length,
          defaultValue: "{{n}} exercise(s)",
        });

  // Just "View"/"Hide" (Antenore 2026-07-04): the card already names what
  // opens, so repeating program/plan in the toggle only padded the row.
  const previewLabel = open
    ? t("mobile.plan.hide", { defaultValue: "Hide" })
    : t("mobile.plan.view", { defaultValue: "View" });

  const cloneLabel = cloning
    ? t("templates.cloning", { defaultValue: "Cloning…" })
    : alreadyCloned
      ? t("templates.alreadyCloned", { defaultValue: "Already cloned" })
      : t("library.clone", { defaultValue: "Clone" });

  const previewProgram = useMemo(
    () =>
      item.kind === "program"
        ? item.source === "personal"
          ? item.program
          : templatePreviewProgram(item.template)
        : null,
    [item],
  );

  return (
    <View style={styles.card}>
      <View style={styles.head}>
        <Text style={styles.name} numberOfLines={3}>
          {name}
        </Text>
        <View style={planStyles.statusChip}>
          <Text style={planStyles.statusChipText}>{chipLabel}</Text>
        </View>
        {onToggleFavorite ? (
          <IconButton
            theme={theme}
            name="star"
            onPress={onToggleFavorite}
            active={favorited}
            disabled={favoriteBusy}
            accessibilityLabel={t("library.favoriteA11y", {
              defaultValue: "Favorite",
            })}
          />
        ) : null}
      </View>
      <Text style={styles.meta}>{meta}</Text>
      {item.source === "official" ? (
        <Text style={styles.desc} numberOfLines={3}>
          {item.template.description}
        </Text>
      ) : null}

      <LibraryItemCardActions
        theme={theme}
        item={item}
        name={name}
        style={styles.actions}
        open={open}
        onTogglePreview={() => setOpen((v) => !v)}
        previewLabel={previewLabel}
        onStart={onStart}
        starting={starting}
        onFollow={onFollow}
        following={following}
        onPublish={onPublish}
        onUnfollow={onUnfollow}
        statusBusy={statusBusy}
        cloneLabel={cloneLabel}
        onClone={onClone}
        cloning={cloning}
        cloneBusy={cloneBusy}
        alreadyCloned={alreadyCloned}
        onEdit={onEdit}
        onDelete={onDelete}
        onDeletePress={onDeletePress}
        confirming={confirming}
      />

      {open ? (
        item.kind === "program" && previewProgram ? (
          <ProgramPreview
            program={previewProgram}
            catalog={catalog}
            resolveName={resolveName}
            unit={unit}
            theme={theme}
            dark={dark}
          />
        ) : item.kind === "plan" ? (
          <PlanPreview
            entries={
              item.source === "personal"
                ? item.plan.entries
                : item.template.entries
            }
            catalog={catalog}
            resolveName={resolveName}
            unit={unit}
            theme={theme}
          />
        ) : null
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, radius: r, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    card: {
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: p.border,
      borderRadius: r.r3,
      backgroundColor: p.surface,
      padding: s.s4,
      gap: s.s1,
    },
    // flex-start so the chip hugs the name's first line when it wraps
    // (centered, it floats mid-block at larger font scales).
    head: {
      flexDirection: "row",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: s.s3,
    },
    name: {
      flex: 1,
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: "600",
    },
    // Same size as PlanRow.metaText — meta stays readable under the
    // system font scale (legibility bounds the density).
    meta: {
      color: p.ink3,
      fontSize: t.sizes.sm,
    },
    desc: {
      color: p.ink2,
      fontSize: t.sizes.sm,
    },
    // Both gaps ≥ 2×hitSlop(8) so adjacent actions' touch zones never
    // overlap — horizontally along the row AND vertically when it wraps
    // (the destructive trash icon sits in this row; a wrapped line must
    // not bleed its slop into it — HR-7 a11y review).
    actions: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      columnGap: s.s5,
      rowGap: s.s5,
    },
  });
}
